(() => {
  'use strict';

  const VERSION = '2026-07-14-v1';
  const ROUTINE_PATCH_COPY =
    'Test each product separately before first full-face use. Apply a small amount behind the ear or on the inner forearm and use it according to its directions (rinse off rinse-off products). Wait 24 hours before full-face use, introduce one new product at a time, and stop if irritation occurs.';
  const PRODUCT_PATCH_COPY =
    'Before first full-face use, test a small amount behind the ear or on the inner forearm and use it according to the directions. Wait 24 hours and stop use if irritation occurs.';

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const getHandle = () => {
    const parts = window.location.pathname.replace(/\/+$/, '').split('/');
    return parts[parts.length - 1] || '';
  };

  const findAccordion = (...labels) => {
    const wanted = labels.map(normalize);
    return [...document.querySelectorAll('details[data-testid="accordion-details"]')].find((details) => {
      const summary = details.querySelector('summary');
      return wanted.includes(normalize(summary?.textContent));
    });
  };

  const setAccordion = (labels, html) => {
    const details = findAccordion(...labels);
    if (!details) return false;
    const content = details.querySelector('.details-content');
    if (!content) return false;
    const key = `${VERSION}:${labels[0]}`;
    if (content.dataset.sovahContentFix === key) return true;
    content.innerHTML = html;
    content.dataset.sovahContentFix = key;
    return true;
  };

  const replaceTextNodes = (root, replacements) => {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(parent.tagName)) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      let value = node.nodeValue || '';
      let next = value;
      for (const [pattern, replacement] of replacements) {
        next = next.replace(pattern, replacement);
      }
      if (next !== value) node.nodeValue = next;
    }
  };

  const removeExactVisibleLabel = (label) => {
    const wanted = normalize(label);
    for (const element of document.querySelectorAll('main h1, main h2, main h3, main h4, main p, main span')) {
      if (normalize(element.textContent) !== wanted) continue;
      const block = element.closest('.text-block') || element.parentElement;
      if (block && !block.closest('.details-content')) block.remove();
    }
  };

  const standardizeStandalonePatchCopy = () => {
    for (const content of document.querySelectorAll('.details-content')) {
      if (content.dataset.sovahStandalonePatch === VERSION) continue;
      const before = content.innerHTML;
      const after = before
        .replace(
          /Perform a patch test before first use, especially if you have sensitive skin\./gi,
          PRODUCT_PATCH_COPY,
        )
        .replace(/Perform a patch test before first use\./gi, PRODUCT_PATCH_COPY);
      if (after !== before) content.innerHTML = after;
      content.dataset.sovahStandalonePatch = VERSION;
    }
  };

  const standardizeRoutinePatchSection = () => {
    const details = findAccordion('How to use', 'How to Use');
    const content = details?.querySelector('.details-content');
    if (!content || content.dataset.sovahRoutinePatch === VERSION) return;

    const candidates = [...content.querySelectorAll('h1,h2,h3,h4,h5,h6,strong,p')];
    let marker = candidates.find((element) => normalize(element.textContent) === 'patch test recommendation');
    if (marker?.tagName === 'STRONG') marker = marker.closest('p') || marker;

    const heading = document.createElement('h4');
    heading.textContent = 'Patch Test Recommendation';
    const paragraph = document.createElement('p');
    paragraph.textContent = ROUTINE_PATCH_COPY;

    if (marker) {
      const parent = marker.parentElement;
      if (parent) {
        let next = marker.nextElementSibling;
        while (next && !/^H[1-6]$/.test(next.tagName)) {
          const remove = next;
          next = next.nextElementSibling;
          remove.remove();
        }
        marker.replaceWith(heading, paragraph);
      }
    } else {
      const caution = candidates.find((element) => normalize(element.textContent) === 'caution');
      if (caution) caution.before(heading, paragraph);
      else content.append(heading, paragraph);
    }

    content.dataset.sovahRoutinePatch = VERSION;
  };

  const normalRoutineContent = () => {
    setAccordion(
      ['Product description'],
      `<p>The <strong>Normal Skin Routine</strong> is a balanced 3-step routine for normal skin. It combines gentle cleansing, hydration, and lightweight moisturising to help keep the skin feeling fresh, comfortable, and balanced.</p>`,
    );
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p><strong>Morning</strong></p>
       <ol>
         <li>Cleanse with <strong>Micellar Cleansing Water</strong>.</li>
         <li>Apply <strong>Hydrating Toner</strong> to the face and neck.</li>
         <li>Apply <strong>Niacinamide Gel Moisturiser</strong> evenly over the face and neck.</li>
         <li>Finish with a separate broad-spectrum sunscreen during the day.</li>
       </ol>
       <p><strong>Evening</strong></p>
       <ol>
         <li>Cleanse with <strong>Micellar Cleansing Water</strong>.</li>
         <li>Apply <strong>Hydrating Toner</strong>.</li>
         <li>Apply <strong>Niacinamide Gel Moisturiser</strong>.</li>
       </ol>
       <h4>Patch Test Recommendation</h4>
       <p>${ROUTINE_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. Avoid direct contact with eyes and mucous membranes. Do not use on irritated or damaged skin. If irritation, redness, dryness, or discomfort occurs, discontinue use. Store in a cool, dry place away from direct sunlight. Keep out of reach of children.</p>
       <h4>Best Combined With</h4>
       <p><strong>Antioxidant Ginkgo Gel Booster</strong> – for an optional antioxidant step.<br><br><strong>Smoothing Eye Cream</strong> – for targeted care around the eye area.</p>`,
    );
    setAccordion(
      ['Key Ingredients', 'Key ingredients', 'Ingredients'],
      `<p><strong>Micellar Cleansing Water</strong><br>Aloe Leaf Juice, Glycerin, Betaine, Chamomile Flower Extract, Linden Flower Extract, and Hyaluronic Acid.</p>
       <p><strong>Hydrating Toner</strong><br>Rose Water, Glycerin, Sodium PCA, Chamomile Flower Extract, Strawberry Extract, and Hyaluronic Acid.</p>
       <p><strong>Niacinamide Gel Moisturiser</strong><br>5% Niacinamide, Glycerin, Ginkgo Biloba Leaf Extract, and Algin.</p>`,
    );
    setAccordion(
      ["What’s Included", "What's Included"],
      `<p><strong>Micellar Cleansing Water</strong><br><strong>Hydrating Toner</strong><br><strong>Niacinamide Gel Moisturiser</strong></p>`,
    );
    setAccordion(
      ['Star features', 'Star Features'],
      `<p>Balanced 3-step routine<br>Gentle daily cleansing<br>Hydration after cleansing<br>Lightweight moisturising care<br>Made for normal skin<br>Easy morning and evening routine</p>`,
    );
  };

  const simpleNormalRoutineContent = () => {
    setAccordion(
      ['Product description'],
      `<p>The <strong>Simple Normal Skin Routine</strong> is an easy 2-step routine for normal skin. <strong>Micellar Cleansing Water</strong> removes make-up and daily impurities without harsh rubbing, while <strong>Moisturising Day Cream</strong> helps hydrate, soften, and support a comfortable skin barrier.</p>`,
    );
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p><strong>Morning</strong></p>
       <ol>
         <li>Cleanse with <strong>Micellar Cleansing Water</strong>.</li>
         <li>Apply <strong>Moisturising Day Cream</strong> evenly over the face and neck.</li>
         <li>Finish with a separate broad-spectrum sunscreen during the day.</li>
       </ol>
       <p><strong>Evening</strong></p>
       <ol>
         <li>Cleanse with <strong>Micellar Cleansing Water</strong>.</li>
         <li>Apply <strong>Moisturising Day Cream</strong> if your skin needs a simple moisturising step.</li>
       </ol>
       <h4>Patch Test Recommendation</h4>
       <p>${ROUTINE_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. Avoid direct contact with eyes and mucous membranes. Do not use on irritated or damaged skin. If irritation or discomfort occurs, discontinue use. Store in a cool, dry place away from direct sunlight. Keep out of reach of children.</p>
       <h4>Best Combined With</h4>
       <p><strong>Hydrating Toner</strong> – for an optional hydration step after cleansing.<br><br><strong>Smoothing Eye Cream</strong> – for targeted care around the eye area.</p>`,
    );
    setAccordion(
      ['Key Ingredients', 'Key ingredients', 'Ingredients'],
      `<p><strong>Micellar Cleansing Water</strong><br>Aloe Leaf Juice, Glycerin, Betaine, Chamomile Flower Extract, Linden Flower Extract, and Hyaluronic Acid.</p>
       <p><strong>Moisturising Day Cream</strong><br>Hyaluronic Acid, Bisabolol, Jojoba Oil, Glycerin, Betaine, and Berry and Fruit Extracts.</p>`,
    );
    setAccordion(
      ["What’s Included", "What's Included"],
      `<p><strong>Micellar Cleansing Water</strong><br><strong>Moisturising Day Cream</strong></p>`,
    );
    setAccordion(
      ['Star features', 'Star Features'],
      `<p>Simple 2-step skincare routine<br>Made for normal skin<br>Gentle daily cleansing<br>Daily hydration and comfort<br>Easy morning and evening routine<br>Suitable for beginners</p>`,
    );
  };

  const sensitiveRoutineContent = () => {
    setAccordion(
      ['Product description'],
      `<p>The <strong>Sensitive Skin Routine</strong> is a hydration-focused routine for skin that feels easily stressed or reactive. Introduce each product separately and gradually. The included facial oil is a rich, fragranced oil containing sweet almond oil, so it may not suit fragrance-sensitive skin or people with an almond allergy.</p>`,
    );
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p><strong>Morning</strong></p>
       <ol>
         <li>Cleanse gently with <strong>Micellar Cleansing Water</strong>.</li>
         <li>Apply <strong>Hydrating Toner</strong>.</li>
         <li>Apply <strong>Hydrating Serum</strong>.</li>
         <li>Finish with a separate broad-spectrum sunscreen during the day.</li>
       </ol>
       <p><strong>Evening</strong></p>
       <ol>
         <li>Cleanse with <strong>Micellar Cleansing Water</strong>.</li>
         <li>Apply <strong>Hydrating Toner</strong>.</li>
         <li>Apply <strong>Hydrating Serum</strong>.</li>
         <li>Apply <strong>Ceramide Barrier Night Cream</strong>.</li>
         <li>If well tolerated, finish with 1–3 drops of <strong>Calming Facial Oil</strong> as the final oil step.</li>
       </ol>
       <h4>Patch Test Recommendation</h4>
       <p>${ROUTINE_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. Avoid direct contact with eyes and mucous membranes. Introduce products one at a time. The facial oil contains fragrance and sweet almond oil; do not use it if you have an almond allergy, and skip it if your skin is highly fragrance-sensitive. If irritation, redness, stinging, or discomfort occurs, discontinue use. Store in a cool, dry place away from direct sunlight.</p>
       <h4>Best Combined With</h4>
       <p>Keep the routine simple. Avoid adding exfoliating acids or other strong treatments until you know the routine is well tolerated.</p>`,
    );
    setAccordion(
      ['Key Ingredients', 'Key ingredients', 'Ingredients'],
      `<p><strong>Micellar Cleansing Water</strong><br>Aloe Leaf Juice, Glycerin, Betaine, Chamomile Flower Extract, Linden Flower Extract, and Hyaluronic Acid.</p>
       <p><strong>Hydrating Toner</strong><br>Rose Water, Glycerin, Sodium PCA, Chamomile Flower Extract, Strawberry Extract, and Hyaluronic Acid.</p>
       <p><strong>Hydrating Serum</strong><br>Aloe Vera, Jojoba Oil, Glycerin, Rose Water, Betaine, Bisabolol, Peony Root Extract, Cloudberry Extract, and Hyaluronic Acid.</p>
       <p><strong>Calming Facial Oil</strong><br>Jojoba Oil, Tomato Fruit Extract, Sea Buckthorn Fruit Extract, Argan Oil, Cloudberry Extract, Vitamin E, Avocado Oil, Sweet Almond Oil, Apricot Kernel Oil, Borage Seed Oil, Evening Primrose Oil, Plum Seed Oil, and Blueberry Seed Oil.</p>
       <p><strong>Ceramide Barrier Night Cream</strong><br>Ceramides, Cocoa Butter, Jojoba Oil, Sunflower Oil, Shea Butter, Peony Root Extract, Elder Fruit Extract, Cranberry Seed Oil, and Hyaluronic Acid.</p>`,
    );
    setAccordion(
      ["What’s Included", "What's Included"],
      `<p><strong>Micellar Cleansing Water</strong><br><strong>Hydrating Toner</strong><br><strong>Hydrating Serum</strong><br><strong>Calming Facial Oil</strong><br><strong>Ceramide Barrier Night Cream</strong></p>`,
    );
    setAccordion(
      ['Star features', 'Star Features'],
      `<p>Hydration-focused routine<br>Gentle daily cleansing<br>Supports skin comfort<br>Products introduced gradually<br>Fragranced facial oil should be used only if tolerated</p>`,
    );
    removeExactVisibleLabel('Nut Free');
  };

  const calmingOilContent = () => {
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p>Apply 1–3 drops of <strong>Calming Facial Oil</strong> to clean skin and gently massage over the face and neck. Use morning and/or evening as the final oil step. Avoid direct contact with the eyes.</p>
       <h4>Patch Test Recommendation</h4>
       <p>${PRODUCT_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. This product contains hemp seed oil, cannabidiol (CBD) and fragrance. If your skin is fragrance-sensitive or highly reactive, introduce it gradually. Discontinue use if irritation occurs. Store in a cool, dry place away from direct sunlight.</p>
       <h4>Best Combined With</h4>
       <p><strong>Hydrating Serum</strong> – apply before the oil for additional hydration.<br><br><strong>Ceramide Barrier Night Cream</strong> – use before the oil in an evening routine.</p>`,
    );
    removeExactVisibleLabel('Nut Free');
  };

  const acneSpotContent = () => {
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p>Apply a small amount of <strong>Acne Spot Care</strong> directly to clean, dry skin on individual blemishes. Start once daily. Increase to twice daily only if your skin tolerates it well. This is a targeted treatment and should not be used as a full-face moisturiser.</p>
       <h4>Patch Test Recommendation</h4>
       <p>${PRODUCT_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. Contains 2% salicylic acid and 3% tea tree oil. Avoid direct contact with eyes and mucous membranes, and do not apply to broken or irritated skin. Avoid using it on the same area at the same time as strong exfoliating treatments. If dryness, stinging, redness, or irritation occurs, reduce use or discontinue. Keep out of reach of children.</p>
       <h4>Best Combined With</h4>
       <p><strong>Purifying Mousse</strong> – for cleansing before targeted application.<br><br><strong>Niacinamide Gel Moisturiser</strong> or <strong>Oil-Free Hydrating Gel</strong> – for lightweight hydration around, not directly over, freshly applied spot care.</p>`,
    );
  };

  const vitaminCContent = () => {
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p>Apply a small amount of <strong>Vitamin C Serum</strong> to clean, dry skin and gently massage over the face and neck. Use preferably in the morning before moisturiser. Finish with a broad-spectrum sunscreen during the day.</p>
       <h4>Patch Test Recommendation</h4>
       <p>${PRODUCT_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. Avoid direct contact with eyes. If irritation occurs, discontinue use. Store in a cool, dry place away from direct sunlight and close the bottle tightly after use. This product contains sweet almond oil.</p>
       <h4>Best Combined With</h4>
       <p><strong>Hydrating Toner</strong> – use before the serum.<br><br><strong>Moisturising Day Cream</strong> – use after the serum.<br><br><strong>Broad-spectrum sunscreen</strong> – use as the final morning step.</p>`,
    );
    setAccordion(
      ['Ingredients', 'Key Ingredients', 'Key ingredients'],
      `<p>Vitamin C derivative (Ascorbyl Glucoside), Ferulic Acid, Sea-Buckthorn Oil, Vitamin E, Jojoba Seed Oil, Sweet Almond Oil, Avocado Oil, Rosehip Seed Oil, Sodium PCA, and Hyaluronic Acid.</p>`,
    );
  };

  const eyeCreamContent = () => {
    setAccordion(
      ['How to use', 'How to Use'],
      `<h4>How to Use</h4>
       <p>Apply a small amount of <strong>Smoothing Eye Cream</strong> around the orbital bone using your ring finger. Gently pat until absorbed. Use morning and evening. Do not apply directly into the eyes or too close to the lash line.</p>
       <h4>Patch Test Recommendation</h4>
       <p>${PRODUCT_PATCH_COPY}</p>
       <h4>Caution</h4>
       <p>For external use only. Avoid direct contact with eyes. If irritation occurs, discontinue use. Store in a cool, dry place away from direct sunlight.</p>
       <h4>Best Combined With</h4>
       <p><strong>Hydrating Serum</strong> – use on the face before eye cream, avoiding direct eye contact.<br><br><strong>Peptide Anti-Aging Serum</strong> – use on the face as part of an anti-aging routine.<br><br><strong>Ceramide Barrier Night Cream</strong> – use on the face after eye cream in the evening.<br><br><strong>All-In-One Facial Oil</strong> – use on the face as the final oil step, avoiding the immediate eye area.</p>`,
    );
  };

  let applying = false;
  const applyFixes = () => {
    if (document.querySelector('.sovah-routine-intro')) return;
    if (applying) return;
    applying = true;
    try {
      const main = document.querySelector('main');
      if (!main || !window.location.pathname.includes('/products/')) return;

      const handle = getHandle();
      replaceTextNodes(main, [
        [/Gluten Fee/g, 'Gluten Free'],
        [/Ses-Buckthorn/g, 'Sea-Buckthorn'],
        [/Ceramide Hydrating Night Cream/g, 'Ceramide Barrier Night Cream'],
        [/Peptide Serum/g, 'Peptide Anti-Aging Serum'],
      ]);

      if (['normal-skin-routine'].includes(handle)) {
        normalRoutineContent();
      } else if (['simple-normal-skin-routine'].includes(handle)) {
        simpleNormalRoutineContent();
      } else if (['sensitive-skin-routine'].includes(handle)) {
        sensitiveRoutineContent();
      } else if (handle === 'calming-facial-oil') {
        calmingOilContent();
      } else if (handle === 'acne-spot-care') {
        acneSpotContent();
      } else if (handle === 'vitamin-c-serum') {
        vitaminCContent();
      } else if (handle === 'smoothing-eye-cream') {
        eyeCreamContent();
      }

      standardizeStandalonePatchCopy();
      if (handle.includes('routine')) standardizeRoutinePatchSection();
    } finally {
      applying = false;
    }
  };

  let timer;
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(applyFixes, 40);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyFixes, { once: true });
  } else {
    applyFixes();
  }

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
